// "Edit FBO details" modal of a ShareJet booking: departure and arrival FBO.
import { useEffect } from 'react';
import { App as AntdApp, Form, Input, Modal, Typography } from 'antd';

import { LIMITS } from '../../data/limits';
import type { AdminBookingDetail } from '../../api/types';
import { extractError, type SubmitFn } from './bookingUpdate';
import { useSingleSubmit } from '../../hooks/useSingleSubmit';

const { Title } = Typography;

export function FboModal({
  open,
  onClose,
  booking,
  onSubmit,
  submitting,
}: {
  open: boolean;
  onClose: () => void;
  booking: AdminBookingDetail;
  onSubmit: SubmitFn;
  submitting: boolean;
}) {
  const { message } = AntdApp.useApp();
  type FboForm = {
    departureFbo: { name: string; address: string; phone: string };
    arrivalFbo: { name: string; address: string; phone: string };
  };
  const [form] = Form.useForm<FboForm>();
  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        departureFbo: {
          name: booking.departureFbo?.name ?? '',
          address: booking.departureFbo?.address ?? '',
          phone: booking.departureFbo?.phone ?? '',
        },
        arrivalFbo: {
          name: booking.arrivalFbo?.name ?? '',
          address: booking.arrivalFbo?.address ?? '',
          phone: booking.arrivalFbo?.phone ?? '',
        },
      });
    }
  }, [open, booking, form]);

  // Regression 28 Sep — one submit at a time.
  const submitGuard = useSingleSubmit();
  const handleOk = submitGuard.wrap(async () => {
    const v = await form.validateFields();
    try {
      await onSubmit({ departureFbo: v.departureFbo, arrivalFbo: v.arrivalFbo });
      message.success('FBO details saved');
      onClose();
    } catch (e) {
      message.error(extractError(e));
    }
  });

  return (
    <Modal
      title="Edit FBO details"
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      confirmLoading={submitting || submitGuard.busy}
      destroyOnClose
      width={640}
    >
      <Form form={form} layout="vertical">
        <Title level={5} style={{ marginTop: 0 }}>
          Departure FBO
        </Title>
        <Form.Item name={['departureFbo', 'name']} label="Name">
          <Input maxLength={LIMITS.name} allowClear />
        </Form.Item>
        <Form.Item name={['departureFbo', 'address']} label="Address">
          <Input.TextArea maxLength={LIMITS.text} showCount autoSize={{ minRows: 1, maxRows: 3 }} allowClear />
        </Form.Item>
        <Form.Item name={['departureFbo', 'phone']} label="Phone">
          <Input maxLength={LIMITS.phone} allowClear />
        </Form.Item>

        <Title level={5}>Arrival FBO</Title>
        <Form.Item name={['arrivalFbo', 'name']} label="Name">
          <Input maxLength={LIMITS.name} allowClear />
        </Form.Item>
        <Form.Item name={['arrivalFbo', 'address']} label="Address">
          <Input.TextArea maxLength={LIMITS.text} showCount autoSize={{ minRows: 1, maxRows: 3 }} allowClear />
        </Form.Item>
        <Form.Item name={['arrivalFbo', 'phone']} label="Phone">
          <Input maxLength={LIMITS.phone} allowClear />
        </Form.Item>
      </Form>
    </Modal>
  );
}
